import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Layout from "../components/Layout";
import {
  FaHome,
  FaShieldAlt,
  FaLock,
  FaEye,
  FaUserCheck,
  FaFileContract,
  FaHandshake,
  FaQuestionCircle,
  FaEnvelope,
  FaCalendarAlt,
  FaExclamationTriangle,
} from "react-icons/fa";
import BreadCrumbImage from "../assets/images/breadcrumb.png";

const PrivacyPolicy = () => {
  const sections = [
    {
      id: "introduction",
      title: "Introduction",
      icon: FaShieldAlt,
      content:
        "Our school is committed to protecting the privacy and security of student, parent, and staff information. This Privacy Policy outlines how we collect, use, disclose, and protect personal information in accordance with applicable laws and regulations.",
      points: [
        "Applies to all information collected through school operations",
        "Covers students, parents, staff, and website visitors",
        "Compliant with education regulations and data protection laws",
      ],
    },
    {
      id: "information-collected",
      title: "Information We Collect",
      icon: FaEye,
      content:
        "We collect information necessary for educational and administrative purposes:",
      listItems: [
        "Student Information: Name, date of birth, address, contact details, academic records, medical information, photographs",
        "Parent/Guardian Information: Names, contact details, occupation, identification documents",
        "Staff Information: Personal details, qualifications, employment history, background checks",
        "Website Usage: IP addresses, browser type, pages visited (through cookies)",
        "Financial Information: Fee payment details, bank information",
      ],
    },
    {
      id: "purpose",
      title: "Purpose of Collection",
      icon: FaUserCheck,
      content:
        "We use collected information for legitimate educational and administrative purposes:",
      points: [
        "Student enrollment and academic management",
        "Communication with parents and guardians",
        "Academic assessment and progress tracking",
        "School administration and operations",
        "Safety and security on school premises",
        "Compliance with legal and regulatory requirements",
      ],
    },
    {
      id: "data-sharing",
      title: "Data Sharing & Disclosure",
      icon: FaHandshake,
      content: "We may share information only under specific circumstances:",
      listItems: [
        "With educational authorities as required by law",
        "With service providers (transport, medical, etc.) under confidentiality agreements",
        "During emergencies involving health or safety",
        "For legal compliance or law enforcement requests",
        "With parent consent for specific purposes",
      ],
    },
    {
      id: "data-security",
      title: "Data Security",
      icon: FaLock,
      content:
        "We implement appropriate security measures to protect personal information:",
      points: [
        "Secure storage of physical and digital records",
        "Access controls and authentication measures",
        "Regular security assessments and updates",
        "Staff training on data protection",
        "Encryption of sensitive information",
      ],
    },
    {
      id: "data-retention",
      title: "Data Retention",
      icon: FaCalendarAlt,
      content: "We retain personal information only as long as necessary:",
      listItems: [
        "Student records: Maintained as per education board requirements",
        "Financial records: 7 years as per statutory requirements",
        "Employee records: Duration of employment plus statutory period",
        "Website logs: 3 months for security purposes",
        "Destruction of records: Secure disposal when no longer needed",
      ],
    },
    {
      id: "rights",
      title: "Your Rights",
      icon: FaFileContract,
      content:
        "Parents and eligible students have certain rights regarding their information:",
      points: [
        "Right to access personal information",
        "Right to request correction of inaccurate data",
        "Right to object to certain processing activities",
        "Right to withdraw consent (where applicable)",
        "Right to lodge complaints with authorities",
      ],
    },
    {
      id: "cookies",
      title: "Cookies & Tracking",
      icon: FaQuestionCircle,
      content: "Our website uses cookies to enhance user experience:",
      listItems: [
        "Essential cookies: Required for website functionality",
        "Analytics cookies: Help us understand website usage",
        "Preference cookies: Remember your settings",
        "You can control cookies through browser settings",
      ],
    },
    {
      id: "third-party",
      title: "Third-Party Links",
      icon: FaExclamationTriangle,
      content: "Our website may contain links to third-party sites:",
      points: [
        "We are not responsible for third-party privacy practices",
        "Review privacy policies of linked sites",
        "External sites have their own terms and conditions",
      ],
    },
    {
      id: "updates",
      title: "Policy Updates",
      icon: FaEnvelope,
      content: "We may update this policy periodically:",
      listItems: [
        "Updates will be posted on our website",
        "Significant changes will be communicated",
        "Current version always available online",
        "Last updated: January 1, 2026",
      ],
    },
  ];

  const importantNotes = [
    "This policy applies to all school operations including online and offline activities",
    "We never sell personal information to third parties",
    "Photographs may be used for school publications with consent",
    "Medical information is handled with special confidentiality",
    "Parents have primary rights regarding student information",
  ];

  const contactInfo = {
    email: "privacy@school.edu",
    phone: "+91 1234567890",
    address: "School Office, During Office Hours",
    officer: "Data Protection Officer",
  };

  return (
    <Layout>
      <Helmet>
        <title>Privacy Policy | School</title>
        <meta
          name="description"
          content="School privacy policy outlining how we collect, use, and protect student, parent, and staff information."
        />
      </Helmet>

      {/* Breadcrumb */}
      <div className="relative bg-gray-900">
        <div className="h-40 md:h-48">
          <img
            src={BreadCrumbImage}
            alt="Breadcrumb"
            className="object-cover w-full h-full opacity-60"
          />
          <div className="absolute inset-0 bg-black/50" />
        </div>

        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <div className="flex items-center mb-2 text-xs text-white">
            <Link
              to="/"
              className="flex items-center transition-colors hover:text-gray-200"
            >
              <FaHome className="w-3 h-3 mr-1" />
              Home
            </Link>
            <span className="mx-1">/</span>
            <span className="font-semibold">Privacy Policy</span>
          </div>
          <h1 className="text-2xl font-bold text-white md:text-3xl">
            Privacy Policy
          </h1>
          <p className="mt-1 text-xs text-gray-300">
            Protecting your information is our priority
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container px-3 py-6 mx-auto md:px-4 md:py-8">
        {/* Introduction Card */}
        <div className="p-5 mb-8 border border-blue-200 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl">
          <div className="flex items-center gap-3 mb-4">
            <FaShieldAlt className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">
              Our Commitment to Privacy
            </h2>
          </div>
          <p className="mb-4 text-gray-700">
            At our school, we are committed to maintaining the privacy and
            security of all personal information in our care. This policy
            explains how we handle information in compliance with applicable
            laws and our educational mission.
          </p>
          <div className="flex items-center gap-2 text-sm text-blue-600">
            <FaCalendarAlt className="w-4 h-4" />
            <span>Last Updated: January 1, 2026</span>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="p-4 mb-8 border border-gray-200 bg-gray-50 rounded-xl">
          <h3 className="mb-3 font-bold text-gray-900">Quick Navigation</h3>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5">
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="px-3 py-2 text-xs text-center text-gray-700 transition-colors bg-white border border-gray-300 rounded-lg hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300"
              >
                {section.title}
              </a>
            ))}
          </div>
        </div>

        {/* Policy Sections */}
        <div className="space-y-6">
          {sections.map((section, index) => (
            <div key={section.id} id={section.id} className="scroll-mt-20">
              <div className="overflow-hidden transition-shadow bg-white border border-gray-200 rounded-xl hover:shadow-sm">
                {/* Section Header */}
                <div className="p-5 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <section.icon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">
                        {index + 1}. {section.title}
                      </h3>
                    </div>
                  </div>
                </div>

                {/* Section Content */}
                <div className="p-5">
                  <p className="mb-4 text-gray-700">{section.content}</p>

                  {section.points && (
                    <ul className="space-y-2">
                      {section.points.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 mt-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                          <span className="text-gray-700">{point}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {section.listItems && (
                    <ul className="space-y-2">
                      {section.listItems.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="font-medium text-blue-600">•</span>
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Important Notes */}
        <div className="p-5 mt-8 border bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 rounded-xl">
          <div className="flex items-center gap-3 mb-4">
            <FaExclamationTriangle className="w-6 h-6 text-amber-600" />
            <h3 className="text-lg font-bold text-gray-900">Important Notes</h3>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            {importantNotes.map((note, index) => (
              <div key={index} className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 mt-2 bg-amber-500 rounded-full flex-shrink-0"></div>
                <p className="text-sm text-gray-700">{note}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Information */}
        <div className="p-5 mt-8 border border-blue-200 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl">
          <h3 className="mb-4 text-lg font-bold text-center text-gray-900">
            Contact for Privacy Concerns
          </h3>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 mb-3 bg-blue-100 rounded-full">
                <FaEnvelope className="w-6 h-6 text-blue-600" />
              </div>
              <div className="text-sm text-gray-600">Email</div>
              <div className="font-medium text-gray-900">
                {contactInfo.email}
              </div>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 mb-3 bg-blue-100 rounded-full">
                <FaShieldAlt className="w-6 h-6 text-blue-600" />
              </div>
              <div className="text-sm text-gray-600">Contact Officer</div>
              <div className="font-medium text-gray-900">
                {contactInfo.officer}
              </div>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 mb-3 bg-blue-100 rounded-full">
                <FaCalendarAlt className="w-6 h-6 text-blue-600" />
              </div>
              <div className="text-sm text-gray-600">Availability</div>
              <div className="font-medium text-gray-900">
                {contactInfo.address}
              </div>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="p-5 mt-8 bg-white border border-gray-200 rounded-xl">
          <div className="text-center">
            <h3 className="mb-3 text-lg font-bold text-gray-900">Summary</h3>
            <p className="mb-4 text-gray-700">
              This Privacy Policy is designed to be transparent about how we
              handle personal information. We regularly review and update our
              practices to ensure compliance with laws and best practices in
              data protection.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg">
              <FaFileContract className="w-4 h-4" />
              <span>
                This policy is part of our school's commitment to transparency
                and protection
              </span>
            </div>
          </div>
        </div>

        {/* Back to Top */}
        <div className="mt-6 text-center">
          <a
            href="#top"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            Back to Top
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 10l7-7m0 0l7 7m-7-7v18"
              />
            </svg>
          </a>
        </div>
      </div>
    </Layout>
  );
};

export default PrivacyPolicy;
